import { LitElement, html, css, unsafeCSS } from 'lit';
import base from '../styles/base.css?inline';
import { INVESTIGATION_TYPES, investigationSuggestions, normalizeFieldOrder } from '@mv/models';
import { api } from '../api.js';
import { formatDate } from '../utils/datetime.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { lucideIcon } from '../icon.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Trash2 } from 'lucide';
import { loadSublocations, SUBLOCATIONS_EVENT } from '../utils/sublocations.js';
import { reportUiStyles, REPORT_STATUS, countFilledFields } from '../utils/report-ui.js';
import './report-setup-dialog.js';
import './report-editor.js';

const TYPE_LABEL = Object.fromEntries(INVESTIGATION_TYPES.map((t) => [t.key, t.label]));

/**
 * report-list — de tab Rapportage in een project (featureplan rapportage §12).
 *
 * Zonder rapporten een tegel die uitlegt wat de pagina doet en laat zien wat er al klaarstaat
 * (veldwerkopdracht, boringen, labresultaten); met rapporten de lijst. 'Nieuw rapport' opent
 * de dialoog, die op basis van de veldwerkopdracht en de boringen de onderzoeken voorstelt.
 *
 * @fires open-map  de adviseur wil deellocaties tekenen of toewijzen
 */
export class ReportList extends LitElement {
  static properties = {
    projectId: { type: String },
    projectBorings: { type: Array },
    _reports: { state: true },
    _sublocations: { state: true },
    _fieldOrder: { state: true },   // genormaliseerde veldwerkopdracht, of null
    _labOrders: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _setupOpen: { state: true },
    _openId: { state: true },       // het rapport dat in de editor openstaat
    _fillOnOpen: { state: true },   // net aangemaakt: de editor vult meteen
  };

  static styles = [css`${unsafeCSS(base)}`, reportUiStyles, css`
    :host { display:block; }
    .head { display:flex; align-items:flex-end; gap:14px; flex-wrap:wrap; margin-bottom:14px; }
    .head > div { flex:1; min-width:240px; display:flex; flex-direction:column; gap:4px; }
    h2 { margin:0; font-size:1.0625rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); }
    .sub { font-size:.8125rem; color:var(--mv-text-sub); }
    .error { color:var(--mv-danger); font-size:.82rem; padding:10px 0; }
    .muted { font-size:.8rem; color:var(--mv-text-muted); }

    /* Lege staat: één grote tegel, klikbaar als geheel. */
    .hero { width:100%; max-width:880px; border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:16px; padding:0;
            cursor:pointer; text-align:left; display:flex; flex-wrap:wrap; overflow:hidden; box-shadow:var(--mv-shadow-sm);
            transition:box-shadow .2s, border-color .2s; font:inherit; color:inherit; }
    .hero:hover { border-color:var(--mv-primary); box-shadow:0 10px 28px var(--mv-primary-shadow); }
    .hero .art { flex:0 0 220px; max-width:100%; background:var(--mv-primary-light); display:flex; align-items:center; justify-content:center; padding:28px 0; }
    .page { width:124px; height:164px; background:var(--mv-surface); border-radius:6px; box-shadow:0 6px 18px rgba(24,28,50,.08);
            padding:16px 14px; display:flex; flex-direction:column; gap:7px; }
    .page i { display:block; height:3px; border-radius:2px; background:var(--mv-border); }
    .page i.t { height:6px; width:60%; background:var(--mv-primary); }
    .page i.s { height:4px; width:40%; background:var(--mv-border); margin-bottom:6px; }
    .page i.b { height:22px; border:1px solid var(--mv-primary-tint); background:var(--mv-primary-light); margin:4px 0; }
    .hero .body { flex:1 1 360px; padding:26px 28px; display:flex; flex-direction:column; gap:16px; }
    .hero h3 { margin:0; font-size:1.125rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); }
    .hero p { margin:6px 0 0; font-size:.8125rem; color:var(--mv-text-sub); line-height:1.55; }
    .ready { display:flex; flex-direction:column; }
    .ready div { display:flex; align-items:center; gap:10px; padding:7px 0; border-top:1px solid var(--mv-border-soft); font-size:.8125rem; }
    .ready div:last-child { border-bottom:1px solid var(--mv-border-soft); }
    .dot { width:7px; height:7px; border-radius:50%; flex:none; background:var(--mv-primary); }
    .dot.later { background:#F6A609; }
    .ready b { font-weight:600; color:var(--mv-text); min-width:140px; }
    .ready span:last-child { color:var(--mv-text-sub); }
    .hero .go { align-self:flex-start; }

    /* Lijst: een kaart per rapport. */
    .rows { display:flex; flex-direction:column; gap:10px; }
    .row { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:14px; padding:16px 18px; display:flex; align-items:center;
           gap:18px; flex-wrap:wrap; cursor:pointer; text-align:left; box-shadow:var(--mv-shadow-sm); font:inherit; color:inherit; width:100%; }
    .row:hover { border-color:var(--mv-primary); }
    .row { box-sizing:border-box; }
    .row:focus-visible { outline:2px solid var(--mv-primary); outline-offset:2px; }
    .weg { display:flex; padding:6px; border:none; border-radius:8px; background:none; color:var(--mv-text-muted); cursor:pointer; }
    .weg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .doc { width:36px; height:44px; flex:none; border-radius:5px; background:#E8EEFD; color:#1D4ED8; font-size:.6rem; font-weight:700;
           display:flex; align-items:flex-end; justify-content:center; padding-bottom:6px; }
    .main { flex:1 1 280px; min-width:0; display:flex; flex-direction:column; gap:5px; }
    .title { font-size:.9rem; font-weight:700; color:var(--mv-text); }
    .chips { display:flex; gap:5px; flex-wrap:wrap; }
    .chips span { font-size:.72rem; font-weight:600; padding:2px 8px; border-radius:999px; background:var(--mv-surface-alt); color:var(--mv-text-sub); }
    .meta { display:flex; flex-direction:column; gap:2px; min-width:110px; }
    .meta span:last-child { font-size:.8125rem; color:var(--mv-text-sub); }
    .mono { font-family:ui-monospace, SFMono-Regular, Menlo, monospace; font-size:.75rem !important; }
  `];

  constructor() {
    super();
    this.projectBorings = [];
    this._reports = [];
    this._sublocations = [];
    this._fieldOrder = null;
    this._labOrders = [];
    this._loading = true;
    this._error = '';
    this._setupOpen = false;
    this._openId = null;
    this._fillOnOpen = false;
    this._onSublocations = (e) => { if (e.detail?.projectId === this.projectId) this._sublocations = e.detail.sublocations; };
    // Vorige/volgende in de browser: het rapport in de URL bepaalt of de editor openstaat.
    this._onPopstate = () => this._volgUrl();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.addEventListener('popstate', this._onPopstate);
    // Een link of herladen met ?rapport=… opent dat rapport meteen.
    this._openId = new URLSearchParams(window.location.search).get('rapport') || null;
  }

  disconnectedCallback() {
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.removeEventListener('popstate', this._onPopstate);
    super.disconnectedCallback();
  }

  /**
   * Een rapport openen zet het in de URL (?rapport=…), met een eigen stap in de geschiedenis.
   * Daardoor brengt 'vorige' je van een rapport terug naar de lijst met rapporten, en niet naar
   * de tab waar je daarvoor was (30-10-2026: dat was Boringen).
   */
  _openRapport(id, vullen = false) {
    this._fillOnOpen = vullen;
    this._openId = id;
    const url = new URL(window.location.href);
    if (url.searchParams.get('rapport') !== id) {
      url.searchParams.set('rapport', id);
      window.history.pushState({ rapport: id }, '', `${url.pathname}${url.search}`);
      this._eigenStap = true;
    }
  }

  /** Terug naar de lijst: de stap van het rapport uit de geschiedenis, of de URL opschonen. */
  _sluitRapport() {
    if (this._eigenStap && window.history.state?.rapport === this._openId) {
      this._eigenStap = false;
      window.history.back();   // _volgUrl sluit de editor en laadt de lijst
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('rapport');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    this._openId = null;
    this._fillOnOpen = false;
    this._load();
  }

  _volgUrl() {
    if (!window.location.pathname.endsWith('/rapportage')) return;
    const id = new URLSearchParams(window.location.search).get('rapport') || null;
    if (id === this._openId) return;
    this._openId = id;
    this._fillOnOpen = false;
    if (!id) this._load();
  }

  updated(changed) {
    if (changed.has('projectId') && this.projectId) this._load();
  }

  async _load() {
    const id = this.projectId;
    this._loading = true;
    this._error = '';
    try {
      // Veldwerkopdracht en labopdrachten zijn alleen voor het voorstel en de tegel: ontbreken
      // ze (of mag dit project ze niet), dan werkt de rest gewoon.
      const [res, sublocations, fo, lab] = await Promise.all([
        api.getReports(id),
        loadSublocations(id).catch(() => []),
        api.getFieldOrder(id).catch(() => null),
        api.getProjectLabOrders(id).catch(() => null),
      ]);
      if (id !== this.projectId) return;
      this._reports = res?.reports || [];
      this._sublocations = sublocations;
      this._fieldOrder = fo?.fieldOrder?.data ? normalizeFieldOrder(fo.fieldOrder.data) : null;
      this._labOrders = lab?.labOrders || [];
    } catch (e) {
      this._error = e.message || 'Rapporten laden mislukt.';
    } finally {
      this._loading = false;
    }
  }

  get _activeBorings() { return (this.projectBorings || []).filter((b) => !b.deletedAt); }

  _created(report) {
    this._setupOpen = false;
    this._reports = [report, ...this._reports];
    this._openRapport(report.id, true);
  }

  render() {
    // Een geopend rapport neemt de hele tab over: de editor heeft de breedte nodig.
    if (this._openId) {
      return html`<report-editor .reportId=${this._openId} .projectId=${this.projectId} .projectBorings=${this.projectBorings}
        .fillOnOpen=${this._fillOnOpen}
        @open-map=${() => this.dispatchEvent(new CustomEvent('open-map', { bubbles: true, composed: true }))}
        @close=${() => this._sluitRapport()}></report-editor>`;
    }
    const any = this._reports.length > 0;
    return html`
      <div class="head">
        <div>
          <h2>Rapportage</h2>
          <span class="sub">Verkennend bodemonderzoek in Word, ingevuld uit de projectdata.</span>
        </div>
        ${any ? html`<button class="btn primary" @click=${() => { this._setupOpen = true; }}>Nieuw rapport</button>` : ''}
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}
      ${this._loading ? html`<div class="muted">Laden…</div>` : any ? this._renderList() : this._renderEmpty()}
      ${this._setupOpen ? html`
        <report-setup-dialog .projectId=${this.projectId} .projectBorings=${this.projectBorings}
          .sublocations=${this._sublocations} .fieldOrder=${this._fieldOrder}
          @close=${() => { this._setupOpen = false; }}
          @report-created=${(e) => this._created(e.detail.report)}></report-setup-dialog>` : ''}
    `;
  }

  /** Wat er al klaarstaat om een rapport mee te vullen. */
  _readiness() {
    const borings = this._activeBorings;
    const peilbuizen = borings.reduce((n, b) => n + (b.peilbuizen || []).length, 0);
    const fo = this._fieldOrder;
    const suggested = investigationSuggestions({ borings, peilbuizen: borings.flatMap((b) => b.peilbuizen || []), fieldOrder: fo })
      .items.filter((i) => i.suggested).length;
    const withResults = this._labOrders.filter((o) => o.resultsStatus === 'available').length;
    const n = (k, een, veel) => `${k} ${k === 1 ? een : veel}`;
    return [
      fo
        ? { label: 'Veldwerkopdracht', value: `${n(countFilledFields(fo), 'veld', 'velden')} · ${n(suggested, 'onderzoek', 'onderzoeken')} voorgesteld`, ok: true }
        : { label: 'Veldwerkopdracht', value: 'Nog niet ingelezen; het voorstel komt dan alleen uit de boringen', ok: false },
      borings.length
        ? { label: 'Boringen', value: `${n(borings.length, 'boring', 'boringen')}, ${n(peilbuizen, 'peilbuis', 'peilbuizen')}`, ok: true }
        : { label: 'Boringen', value: 'Nog geen boringen; die tabellen vullen zich later', ok: false },
      withResults
        ? { label: 'Labresultaten', value: `Binnen voor ${n(withResults, 'labopdracht', 'labopdrachten')}`, ok: true }
        : { label: 'Labresultaten', value: this._labOrders.length ? 'Nog niet binnen; die tabellen vullen zich later' : 'Nog geen labopdracht; die tabellen vullen zich later', ok: false },
    ];
  }

  _renderEmpty() {
    return html`
      <button class="hero" @click=${() => { this._setupOpen = true; }}>
        <div class="art" aria-hidden="true">
          <div class="page"><i class="t"></i><i class="s"></i><i></i><i></i><i style="width:80%"></i><i class="b"></i><i></i><i style="width:70%"></i></div>
        </div>
        <div class="body">
          <div>
            <h3>Maak je eerste rapport</h3>
            <p>Je kiest welke onderzoeken erin komen. GeoVoice zet de hoofdstukken klaar en vult ze met wat al in het project staat.</p>
          </div>
          <div class="ready">
            ${this._readiness().map((r) => html`
              <div><span class="dot ${r.ok ? '' : 'later'}"></span><b>${r.label}</b><span>${r.value}</span></div>`)}
          </div>
          <span class="btn primary go">Rapport aanmaken →</span>
        </div>
      </button>`;
  }

  async _verwijder(r) {
    const ok = await confirmDialog({
      title: `${r.title || 'Rapport'} verwijderen?`,
      message: 'Het rapport en alles wat je erin hebt ingevuld verdwijnen uit de lijst. Weet je het zeker?',
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteReport(r.id);
      this._reports = this._reports.filter((x) => x.id !== r.id);
    } catch (e) {
      this._error = e.message || 'Verwijderen mislukt';
    }
  }

  _renderList() {
    return html`
      <div class="rows">
        ${this._reports.map((r) => html`
          <!-- Een div en geen button: er staat een eigen knop in (verwijderen), en een knop in
               een knop mag niet. -->
          <div class="row" role="button" tabindex="0" title="Openen in de editor"
               @click=${() => this._openRapport(r.id)}
               @keydown=${(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this._openRapport(r.id); } }}>
            <span class="doc">DOCX</span>
            <span class="main">
              <span class="title">${r.title || 'Rapport'}</span>
              <span class="chips">${(r.investigationTypes || []).map((t) => html`<span>${TYPE_LABEL[t] || t}</span>`)}</span>
            </span>
            ${r.reference ? html`<span class="meta"><span class="caps">Kenmerk</span><span class="mono">${r.reference}</span></span>` : ''}
            <span class="meta"><span class="caps">Aangemaakt</span><span>${formatDate(r.createdAt)} · v${r.version}</span></span>
            <span class="status-pill ${REPORT_STATUS[r.status]?.cls || ''}">${REPORT_STATUS[r.status]?.label || r.status}</span>
            <button class="weg" title="Rapport verwijderen" aria-label=${`${r.title || 'Rapport'} verwijderen`}
                    @click=${(e) => { e.stopPropagation(); this._verwijder(r); }}>${unsafeSVG(lucideIcon(Trash2, 16))}</button>
          </div>`)}
      </div>`;
  }
}

customElements.define('report-list', ReportList);
